import { Inject, Injectable } from '@angular/core';
import {
  ListSalesOrderRequestFilters,
  ListSalesOrderRequestFiltersInterface,
  ListSalesOrderRequestSortDirection,
  ListSalesOrderRequestSortField,
  ListSalesOrderRequestSortOption,
  ListSalesOrderRequestSortOptionInterface,
  Order,
  SalesOrdersService,
  Status,
} from '@vendasta/sales-orders';
import { LoadRequest, SortDirection, SortSetting } from '@vendasta/va-filter2-table';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { calculateContractDurationMonths } from './conversion-utils';
import { BaseSalesOrdersTableService, ForcedFilterOptions, OrdersRow } from './interface';

import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Salesperson, SalesTeamService } from '@vendasta/sales';
import { PARTNER_ID_TOKEN } from '../external-dependencies/provider-tokens';

enum SalesOrdersTableTab {
  ALL = 0,
  PENDING = 1,
  APPROVED = 2,
  CANCELLATION_REQUESTS = 3,
  ERRORS = 4,
}
@Injectable()
export class SalesOrdersTableService implements BaseSalesOrdersTableService {
  private readonly totalResults$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly totalResults$: Observable<number> = this.totalResults$$.asObservable();

  public salesOrdersTab$$: BehaviorSubject<SalesOrdersTableTab> = new BehaviorSubject<SalesOrdersTableTab>(null);

  public stats$: Observable<{ [key: string]: number }>;
  public presetViews: string;

  private forcedFilterOptions: ForcedFilterOptions;

  constructor(
    private readonly salesOrderService: SalesOrdersService,
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    private readonly translateService: TranslateService,
    private readonly salesTeamService: SalesTeamService,
    private readonly snackbarService: SnackbarService,
  ) {}

  initialize(forcedFilterOptions: ForcedFilterOptions): void {
    this.forcedFilterOptions = forcedFilterOptions;
    this.stats$ = this.partnerId$.pipe(
      switchMap((partnerId) =>
        this.salesOrderService.getStatusCounts(partnerId, {
          marketIds: this.forcedFilterOptions.marketIds,
          salespersonId: this.forcedFilterOptions.salespersonId,
        }),
      ),
      map((counts) => counts || {}),
      shareReplay(1),
    );

    const currentDate = new Date();
    const nextDate = new Date(currentDate.setMonth(currentDate.getMonth() + 3));

    this.presetViews = JSON.stringify([
      {
        viewId: 'upcomingExpiriesPreset',
        viewName: this.translateService.instant('FRONTEND.SALES_UI.TABLE_PRESETS.UPCOMING_EXPIRATION'),
        viewDescription: this.translateService.instant('FRONTEND.SALES_UI.TABLE_PRESETS.UPCOMING_EXPIRATION_DESC'),
        attributeFiltersJson: JSON.stringify({
          expiryDateFrom: currentDate,
          expiryDateTo: nextDate,
        }),
        sortingJson: `{
          "expiry": "asc"
        }`,
      },
    ]);
  }

  /* tslint:disable */
  static convertFilters(
    filters: any,
    teamSalesPersonIds: string[],
    forcedMarkets?: string[],
    forcedSalesperson?: string,
  ): ListSalesOrderRequestFiltersInterface {
    const f: ListSalesOrderRequestFiltersInterface = {};
    f.statuses = [];
    f.created = {};
    f.requestedActivation = {};
    f.expiryDate = {};

    if (filters['activated']) {
      f.statuses.push(Status.FULFILLED);
    }
    if (filters['activation-errors']) {
      f.statuses.push(Status.ACTIVATION_ERRORS);
    }
    if (filters['approved']) {
      f.statuses.push(Status.APPROVED);
    }
    if (filters['archived']) {
      f.statuses.push(Status.ARCHIVED);
    }
    if (filters['awaiting-customer-approval']) {
      f.statuses.push(Status.SUBMITTED_FOR_CUSTOMER_APPROVAL);
    }
    if (filters['declined']) {
      f.statuses.push(Status.DECLINED);
    }
    if (filters['pending']) {
      f.statuses.push(Status.SUBMITTED);
    }
    if (filters['resubmitted']) {
      f.statuses.push(Status.RESUBMITTED);
    }
    if (filters['processing']) {
      f.statuses.push(Status.PROCESSING);
    }
    if (filters['drafted']) {
      f.statuses.push(Status.DRAFTED);
    }
    if (filters['cancellation-requested']) {
      f.statuses.push(Status.CANCELLATION_REQUESTED);
    }
    if (filters['cancelled']) {
      f.statuses.push(Status.CANCELLED);
    }
    if (filters['scheduled-activation']) {
      f.statuses.push(Status.SCHEDULED_ACTIVATION);
    }

    if (forcedMarkets) {
      f.marketIds = forcedMarkets;
    } else if (filters['market']) {
      f.marketIds = [filters['market'].marketId];
    }

    if (forcedSalesperson) {
      f.salespersonIds = [forcedSalesperson];
    } else if (filters['salesPeople']) {
      const salespersonId = filters['salesPeople'].salespersonId;
      f.salespersonIds = teamSalesPersonIds.includes(salespersonId)
        ? teamSalesPersonIds
        : [...teamSalesPersonIds, salespersonId];
    } else if (filters['salesTeam']) {
      f.salespersonIds = teamSalesPersonIds.length === 0 ? ['EMPTY'] : teamSalesPersonIds;
    }

    if (filters['submittedDateFromControl']) {
      f.created.beginRange = filters['submittedDateFromControl'];
    }
    if (filters['submittedDateToControl']) {
      f.created.endRange = filters['submittedDateToControl'];
    }

    if (filters['contractStartFrom']) {
      f.requestedActivation.beginRange = filters['contractStartFrom'];
    }
    if (filters['contractStartToControl']) {
      f.requestedActivation.endRange = filters['contractStartToControl'];
    }

    if (filters['expiryDateFrom']) {
      f.expiryDate.beginRange = filters['expiryDateFrom'];
    }
    if (filters['expiryDateTo']) {
      f.expiryDate.endRange = filters['expiryDateTo'];
    }

    if (filters['tag']) {
      f.tags = [filters['tag']];
    }
    if (filters['untagged']) {
      f.untagged = true;
    }

    if (filters['businessId']) {
      f.businessId = filters['businessId'];
    }

    if (filters['orderId']) {
      f.orderId = filters['orderId'];
    }

    if (filters['package']) {
      f.packageId = filters['package'].packageId;
    }

    return f;
  }

  private static extractSalespersonIds(salespeople: Salesperson[]): string[] {
    const sps = salespeople || [];
    return sps.map((sp) => sp.salespersonId);
  }

  static convertSortOptions(sort: SortSetting[]): ListSalesOrderRequestSortOption {
    let s: ListSalesOrderRequestSortOptionInterface = null;
    if (!!sort && sort.length > 0) {
      s = {};
      s.direction =
        sort[0].direction == SortDirection.SORT_DIRECTION_ASCENDING
          ? ListSalesOrderRequestSortDirection.ASCENDING
          : ListSalesOrderRequestSortDirection.DESCENDING;
      switch (sort[0].field) {
        case 'status':
          s.field = ListSalesOrderRequestSortField.STATUS;
          break;
        case 'requestedActivation':
          s.field = ListSalesOrderRequestSortField.REQUESTED_ACTIVATION;
          break;
        case 'created':
          s.field = ListSalesOrderRequestSortField.CREATED;
          break;
        case 'expiry':
          s.field = ListSalesOrderRequestSortField.EXPIRY_DATE;
          break;
        default:
          s = null;
      }
    }
    if (s) {
      return new ListSalesOrderRequestSortOption(s);
    }
    return null;
  }
  /* tslint:enable */

  load(request: LoadRequest): Observable<OrdersRow[]> {
    const sorting = SalesOrdersTableService.convertSortOptions(request.sortingOptions);
    const cursor = btoa((request.pageIndex * request.pageSize).toString());
    const salesPersonIdsBelongingToTeam$ = request.filters?.salesTeam
      ? this.salesTeamService.getMembers(request.filters.salesTeam.id).pipe(catchError(() => of([])))
      : of([]);
    return combineLatest([this.partnerId$, salesPersonIdsBelongingToTeam$, this.salesOrdersTab$$]).pipe(
      switchMap(([partnerId, salesPersonIds, salesOrdersTab]) => {
        const teamSalesPersonIds = SalesOrdersTableService.extractSalespersonIds(salesPersonIds);
        this.adjustStatusFiltersForTabs(request, salesOrdersTab);
        const filters = SalesOrdersTableService.convertFilters(
          request.filters,
          teamSalesPersonIds,
          this.forcedFilterOptions.marketIds,
          this.forcedFilterOptions.salespersonId,
        );
        const apiFilters = new ListSalesOrderRequestFilters(filters);
        return this.salesOrderService.list(partnerId, apiFilters, sorting, cursor, request.pageSize);
      }),
      take(1),
      map((results) => {
        if (!results || !results.results) {
          this.totalResults$$.next(results.totalResults);
          return [];
        }
        this.totalResults$$.next(results.totalResults);
        return results.results.map((o: Order) => ({
          orderId: o.orderId,
          salespersonId: o.salespersonId,
          salesperson: o.salesperson,
          partnerId: o.partnerId,
          businessId: o.businessId,
          business: o.business,
          marketId: o.marketId,
          status: o.status,
          requestedActivation: o.requestedActivation,
          created: o.created,
          tags: o.tags,
          contractDurationInMonths: calculateContractDurationMonths(o.contractDuration),
          expiryDate: o.expiryDate,
          fulfillmentOrderStatus: o.fulfillmentStatus,
          hasFulfillment: o.hasFulfillment,
          isEmpty: !o.lineItems?.length,
        }));
      }),
      catchError((error) => {
        if (error.status === 403) {
          this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.ORDER_PERMISSION_ERROR');
          return of([]);
        }
      }),
    );
  }

  adjustStatusFiltersForTabs(request: LoadRequest, salesOrdersTab: SalesOrdersTableTab): void {
    switch (salesOrdersTab) {
      case SalesOrdersTableTab.PENDING:
        if (
          !request.filters['resubmitted'] &&
          !request.filters['pending'] &&
          !request.filters['awaiting-customer-approval']
        ) {
          request.filters['resubmitted'] = true;
          request.filters['pending'] = true;
          request.filters['awaiting-customer-approval'] = true;
        }
        break;
      case SalesOrdersTableTab.APPROVED:
        request.filters['approved'] = true;
        break;
      case SalesOrdersTableTab.CANCELLATION_REQUESTS:
        request.filters['cancellation-requested'] = true;
        break;
      case SalesOrdersTableTab.ERRORS:
        request.filters['activation-errors'] = true;
        break;
    }
  }
}
